import { assertNever } from "@redotech/util/type";
import type {
  AnalyticsBooleanExpression,
  AnalyticsExpression,
} from "../analytics-query/analytics-query-expression-types";
import { isAnalyticsBooleanConnective } from "../analytics-query/analytics-query-function-registry";
import type {
  AnalyticsQuery,
  AnalyticsValueName,
} from "../analytics-query/analytics-query-types";

export type AnalyticsQueryPath = readonly (string | number)[];

type VisitedNode = AnalyticsExpression | AnalyticsBooleanExpression;

/**
 * Visits `node` and every expression nested in it with its path, parents first.
 * `inBooleanSlot` is true where the grammar needs a boolean: connective args, `when`, `only`, `every`/`some` operands.
 */
export function visitNodes(
  node: VisitedNode,
  path: AnalyticsQueryPath,
  visit: (
    node: VisitedNode,
    path: AnalyticsQueryPath,
    inBooleanSlot: boolean,
  ) => void,
  inBooleanSlot = false,
): void {
  visit(node, path, inBooleanSlot);
  const child = (
    next: VisitedNode,
    nextInBooleanSlot: boolean,
    ...segments: readonly (string | number)[]
  ): void => visitNodes(next, [...path, ...segments], visit, nextInBooleanSlot);
  switch (node.kind) {
    case "ref":
      return;
    case "constant":
      return;
    case "call": {
      const booleanArgs = isAnalyticsBooleanConnective(node.fn);
      for (const [index, arg] of node.args.entries()) {
        child(arg, booleanArgs, "args", index);
      }
      return;
    }
    case "categorize":
      for (const [index, branch] of node.cases.entries()) {
        child(branch.when, true, "cases", index, "when");
        child(branch.value, false, "cases", index, "value");
      }
      child(node.otherwise, false, "otherwise");
      return;
    case "aggregate":
      child(node.of, node.op === "every" || node.op === "some", "of");
      if (node.only !== undefined) {
        child(node.only, true, "only");
      }
      if ("per" in node && node.per?.keys !== undefined) {
        for (const [index, key] of node.per.keys.entries()) {
          child(key, false, "per", "keys", index);
        }
      }
      if ("by" in node && node.by !== undefined) {
        for (const [index, term] of node.by.entries()) {
          child(term.of, false, "by", index, "of");
        }
      }
      if ("sequence" in node && node.sequence !== undefined) {
        for (const [index, term] of node.sequence.entries()) {
          child(term.of, false, "sequence", index, "of");
        }
      }
      return;
    case "relative":
      for (const [index, term] of node.sequence.entries()) {
        child(term.of, false, "sequence", index, "of");
      }
      for (const [index, among] of (node.among ?? []).entries()) {
        child(among, false, "among", index);
      }
      if ("of" in node && typeof node.of !== "string") {
        child(node.of, false, "of");
      }
      if (node.only !== undefined) {
        child(node.only, true, "only");
      }
      return;
    default:
      return assertNever(node);
  }
}

/** Where a query names catalog identity. `per.as` names result key columns, which may be catalog names. */
export const AnalyticsIdentityPosition = {
  RefName: "ref.name",
  RefRoute: "ref.route",
  PerEntity: "aggregate.per.entity",
  PerAs: "aggregate.per.as",
  Column: "columns.name",
  Filter: "filters",
  Sort: "sorts.name",
} as const;

export type AnalyticsIdentityPosition =
  (typeof AnalyticsIdentityPosition)[keyof typeof AnalyticsIdentityPosition];

/** One place a query names catalog identity. `path` points at the name, route or entity itself. */
export type AnalyticsIdentitySite =
  | {
      readonly position: typeof AnalyticsIdentityPosition.RefName;
      readonly path: AnalyticsQueryPath;
      readonly name: AnalyticsValueName;
      /** The route on the same ref node, if any. */
      readonly route?: string;
      readonly inBooleanSlot: boolean;
    }
  | {
      readonly position: typeof AnalyticsIdentityPosition.RefRoute;
      readonly path: AnalyticsQueryPath;
      readonly name: AnalyticsValueName;
      readonly route: string;
    }
  | {
      readonly position: typeof AnalyticsIdentityPosition.PerEntity;
      readonly path: AnalyticsQueryPath;
      readonly entity: string;
    }
  | {
      readonly position:
        | typeof AnalyticsIdentityPosition.PerAs
        | typeof AnalyticsIdentityPosition.Column
        | typeof AnalyticsIdentityPosition.Filter
        | typeof AnalyticsIdentityPosition.Sort;
      readonly path: AnalyticsQueryPath;
      readonly name: AnalyticsValueName;
    };

/** Yields the identity sites inside one expression: ref names and routes, `per.entity` and `per.as`. */
export function visitExpressionIdentities(
  expression: AnalyticsExpression | AnalyticsBooleanExpression,
  path: AnalyticsQueryPath,
  visit: (site: AnalyticsIdentitySite) => void,
): void {
  visitNodes(expression, path, (node, nodePath, inBooleanSlot) => {
    if (node.kind === "ref") {
      visit({
        position: AnalyticsIdentityPosition.RefName,
        path: [...nodePath, "name"],
        name: node.name,
        ...(node.route === undefined ? {} : { route: node.route }),
        inBooleanSlot,
      });
      if (node.route !== undefined) {
        visit({
          position: AnalyticsIdentityPosition.RefRoute,
          path: [...nodePath, "route"],
          name: node.name,
          route: node.route,
        });
      }
      return;
    }
    if (node.kind !== "aggregate" || !("per" in node)) {
      return;
    }
    if (node.per?.entity !== undefined) {
      visit({
        position: AnalyticsIdentityPosition.PerEntity,
        path: [...nodePath, "per", "entity"],
        entity: node.per.entity,
      });
    }
    for (const [asIndex, name] of (node.per?.as ?? []).entries()) {
      visit({
        position: AnalyticsIdentityPosition.PerAs,
        path: [...nodePath, "per", "as", asIndex],
        name,
      });
    }
  });
}

/** Yields every identity site: definitions first, then columns, filters and sorts. Local names are yielded too. */
export function visitQueryIdentities(
  query: AnalyticsQuery,
  visit: (site: AnalyticsIdentitySite) => void,
): void {
  for (const [index, definition] of query.definitions.entries()) {
    visitExpressionIdentities(
      definition.expression,
      ["definitions", index, "expression"],
      visit,
    );
  }
  for (const [index, column] of query.columns.entries()) {
    visit({
      position: AnalyticsIdentityPosition.Column,
      path: ["columns", index, "name"],
      name: column.name,
    });
  }
  for (const [index, name] of (query.filters ?? []).entries()) {
    visit({
      position: AnalyticsIdentityPosition.Filter,
      path: ["filters", index],
      name,
    });
  }
  for (const [index, term] of (query.sorts ?? []).entries()) {
    visit({
      position: AnalyticsIdentityPosition.Sort,
      path: ["sorts", index, "name"],
      name: term.name,
    });
  }
}
